import { useState, useEffect } from "react";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import { User, CreditCard, Bell, Shield, LogOut, Loader2, HelpCircle, Trash2, AlertTriangle, Download, Share } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import CommandLayout from "@/components/CommandLayout";
import AuthModal from "@/components/AuthModal";
import HelpFAQ from "@/components/HelpFAQ";
import { useShieldCredits } from "@/hooks/useShieldCredits";
import { useQueryClient } from "@tanstack/react-query";
import ShieldCreditsCounter from "@/components/ShieldCreditsCounter";
import SettingsBillingSection from "@/components/SettingsBillingSection";
import RedeemCodeSection from "@/components/RedeemCodeSection";
import ResearchPartnershipCard from "@/components/ResearchPartnershipCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || (navigator as any).standalone === true;

const SettingsPage = () => {
  const { user, signOut, session } = useAuth();
  const { credits, isPaidUser, isLoading: creditsLoading, accountType } = useShieldCredits();
  const queryClient = useQueryClient();
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteFinal, setShowDeleteFinal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [appInstalled, setAppInstalled] = useState(false);
  const [showIOSInstall, setShowIOSInstall] = useState(false);
  const [researchConsent, setResearchConsent] = useState(false);
  const [consentLoading, setConsentLoading] = useState(true);

  useEffect(() => {
    if (isStandalone()) {
      setAppInstalled(true);
      return;
    }
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    const installedHandler = () => setAppInstalled(true);
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", installedHandler);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  // Load research consent from profile
  useEffect(() => {
    if (!user) return;
    const loadConsent = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("research_consent" as any)
        .eq("user_id", user.id)
        .single();
      if (data && typeof (data as any).research_consent === "boolean") {
        setResearchConsent((data as any).research_consent);
      }
      setConsentLoading(false);
    };
    loadConsent();
  }, [user]);

  const handleInstallApp = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") setAppInstalled(true);
      setDeferredPrompt(null);
    } else if (isIOS()) {
      setShowIOSInstall(true);
    } else {
      toast.info("Open your browser menu (⋮) and select 'Install app' or 'Add to Home Screen'");
    }
  };

  const handleCheckout = async (type: "starter" | "pro" | "refill") => {
    if (!user || !session) {
      setShowAuthModal(true);
      return;
    }

    setIsUpgrading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: { type },
      });

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to start checkout");
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user || !session) return;

    setIsDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-account", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      // Clear all React Query cache so no stale data persists
      queryClient.clear();

      // Clear any pending analysis or return URL from localStorage
      localStorage.removeItem("greyrock_pending_analysis");
      localStorage.removeItem("greyrock_return_url");

      toast.success("Account deleted successfully. Goodbye.");
      await signOut();
    } catch (error) {
      console.error("Delete account error:", error);
      toast.error("Failed to delete account. Please try again.");
    } finally {
      setIsDeleting(false);
      setShowDeleteFinal(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    toast.success("Signed out successfully");
  };

  if (!user) {
    return (
      <CommandLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6">
          <div className="glass-card p-8 text-center max-w-md">
            <Shield className="w-16 h-16 mx-auto mb-4 text-cyan opacity-60" />
            <h2 className="text-2xl font-bold mb-2">Settings</h2>
            <p className="text-muted-foreground mb-6">
              Sign in to manage your account and preferences.
            </p>
            <button
              onClick={() => setShowAuthModal(true)}
              className={cn(
                "px-6 py-3 rounded-xl font-semibold",
                "bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground",
                "hover:scale-[1.02] transition-transform"
              )}
            >
              Sign In
            </button>
          </div>
        </div>
        <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />
      </CommandLayout>
    );
  }

  return (
    <CommandLayout>
      <PageBreadcrumb items={[{ label: "Dashboard", path: "/dashboard" }, { label: "Settings" }]} />
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold mb-2">
            <span className="text-foreground">Set</span>
            <span className="text-gradient-cyan">tings</span>
          </h1>
          <p className="text-muted-foreground">
            Manage your account and preferences
          </p>
        </div>

        {/* Account Section */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-cyan/10">
              <User className="w-5 h-5 text-cyan" />
            </div>
            <h2 className="font-semibold text-lg">Account</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-xl bg-charcoal-medium">
              <div>
                <p className="font-medium">Email</p>
                <p className="text-sm text-muted-foreground">{user.email}</p>
              </div>
            </div>

            <ShieldCreditsCounter
              credits={credits}
              isPaidUser={isPaidUser}
              isLoading={creditsLoading}
            />
          </div>
        </div>

        {/* Subscription Section */}
        <SettingsBillingSection
          user={user}
          session={session}
          isUpgrading={isUpgrading}
          setIsUpgrading={setIsUpgrading}
          setShowAuthModal={setShowAuthModal}
        />

        {/* Redeem Code Section */}
        <RedeemCodeSection />

        {/* Help & FAQ Section */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-cyan/10">
              <HelpCircle className="w-5 h-5 text-cyan" />
            </div>
            <h2 className="font-semibold text-lg">Help & FAQ</h2>
          </div>

          <HelpFAQ />
        </div>

        {/* Notifications Section */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-cyan/10">
              <Bell className="w-5 h-5 text-cyan" />
            </div>
            <h2 className="font-semibold text-lg">Notifications</h2>
          </div>

          <div className="flex items-center justify-between p-4 rounded-xl bg-charcoal-medium">
            <div>
              <p className="font-medium">Email Notifications</p>
              <p className="text-sm text-muted-foreground">
                Receive updates and tips
              </p>
            </div>
            <div className="relative">
              <input
                type="checkbox"
                defaultChecked
                className="sr-only peer"
                id="notifications"
              />
              <label
                htmlFor="notifications"
                className={cn(
                  "block w-11 h-6 rounded-full cursor-pointer transition-colors",
                  "bg-charcoal-light peer-checked:bg-cyan"
                )}
              />
              <span
                className={cn(
                  "absolute left-1 top-1 w-4 h-4 rounded-full bg-white transition-transform",
                  "peer-checked:translate-x-5"
                )}
              />
            </div>
          </div>
        </div>


        {/* Research Partnership */}
        <ResearchPartnershipCard
          userId={user.id}
          initialConsent={researchConsent}
          isLoading={consentLoading}
        />

        {/* Install App Section */}
        {!appInstalled && (
          <div className="glass-card p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg bg-cyan/10">
                <Download className="w-5 h-5 text-cyan" />
              </div>
              <h2 className="font-semibold text-lg">Install App</h2>
            </div>

            <div className="flex items-center justify-between p-4 rounded-xl bg-charcoal-medium">
              <div>
                <p className="font-medium">Install App to Home Screen</p>
                <p className="text-sm text-muted-foreground">
                  Get instant access and a native app experience
                </p>
              </div>
              <button
                onClick={handleInstallApp}
                className={cn(
                  "px-4 py-2 rounded-lg font-semibold text-sm",
                  "bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground",
                  "hover:scale-[1.02] transition-transform"
                )}
              >
                Install
              </button>
            </div>
          </div>
        )}

        {/* Data Management (GDPR) */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-destructive/10">
              <AlertTriangle className="w-5 h-5 text-destructive" />
            </div>
            <h2 className="font-semibold text-lg">Data Management</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-xl bg-charcoal-medium">
              <div>
                <p className="font-medium">Wipe Analysis History</p>
                <p className="text-sm text-muted-foreground">
                  Permanently delete all your neutralization records
                </p>
              </div>
              <button
                onClick={async () => {
                  if (!confirm("Delete ALL your analysis history? This cannot be undone.")) return;
                  try {
                    const { error } = await supabase
                      .from("neutralizations")
                      .delete()
                      .eq("user_id", user!.id);
                    if (error) throw error;
                    toast.success("Analysis history wiped.");
                  } catch {
                    toast.error("Failed to wipe history.");
                  }
                }}
                className={cn(
                  "px-4 py-2 rounded-lg font-semibold text-sm",
                  "bg-destructive/20 border border-destructive/40 text-destructive",
                  "hover:bg-destructive/30 transition-all"
                )}
              >
                Wipe History
              </button>
            </div>

            <div className="flex items-center justify-between p-4 rounded-xl bg-charcoal-medium">
              <div>
                <p className="font-medium">Delete Account</p>
                <p className="text-sm text-muted-foreground">
                  Permanently erase your account and all associated data
                </p>
              </div>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className={cn(
                  "px-4 py-2 rounded-lg font-semibold text-sm",
                  "bg-destructive/20 border border-destructive/40 text-destructive",
                  "hover:bg-destructive/30 transition-all"
                )}
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>

        {/* Sign Out */}
        <button
          onClick={handleSignOut}
          className={cn(
            "w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-medium",
            "bg-charcoal-medium hover:bg-charcoal-light border border-glass-border",
            "text-muted-foreground hover:text-foreground transition-colors"
          )}
        >
          <LogOut className="w-5 h-5" />
          Sign Out
        </button>

      </div>

      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />

      {/* First confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="bg-charcoal-dark border-glass-border max-w-md text-center">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-destructive" />
              </div>
            </div>
            <DialogTitle className="text-xl font-bold text-foreground">
              Delete Your Account?
            </DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            This will permanently delete your profile, all vault documents, and scratchpad notes. This action cannot be undone.
          </p>
          <div className="flex gap-3 mt-4">
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="flex-1 py-3 rounded-xl font-medium bg-charcoal-medium border border-glass-border text-foreground hover:bg-charcoal-light transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                setShowDeleteConfirm(false);
                setShowDeleteFinal(true);
              }}
              className="flex-1 py-3 rounded-xl font-medium bg-destructive/20 border border-destructive/40 text-destructive hover:bg-destructive/30 transition-colors"
            >
              Yes, Delete
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Final confirmation */}
      <Dialog open={showDeleteFinal} onOpenChange={setShowDeleteFinal}>
        <DialogContent className="bg-charcoal-dark border-destructive/30 max-w-md text-center">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-destructive">
              ⚠️ Final Confirmation
            </DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Are you absolutely sure? All your data will be permanently erased. There is no way to recover your account.
          </p>
          <div className="flex gap-3 mt-4">
            <button
              onClick={() => setShowDeleteFinal(false)}
              className="flex-1 py-3 rounded-xl font-medium bg-charcoal-medium border border-glass-border text-foreground hover:bg-charcoal-light transition-colors"
            >
              Go Back
            </button>
            <button
              onClick={handleDeleteAccount}
              disabled={isDeleting}
              className="flex-1 py-3 rounded-xl font-bold bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors disabled:opacity-50"
            >
              {isDeleting ? (
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              ) : (
                "Delete Forever"
              )}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* iOS Install Overlay */}
      <Dialog open={showIOSInstall} onOpenChange={setShowIOSInstall}>
        <DialogContent className="bg-charcoal-dark border-glass-border max-w-sm text-center">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-foreground">Install GreyRock AI</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 text-left">
            <div className="flex items-start gap-3 p-3 rounded-xl bg-charcoal-medium">
              <div className="w-8 h-8 rounded-full bg-cyan/20 flex items-center justify-center shrink-0 text-cyan font-bold text-sm">1</div>
              <div>
                <p className="text-sm font-medium text-foreground">Tap the Share button</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Share className="w-3 h-3 text-cyan" /> at the bottom of Safari
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-xl bg-charcoal-medium">
              <div className="w-8 h-8 rounded-full bg-cyan/20 flex items-center justify-center shrink-0 text-cyan font-bold text-sm">2</div>
              <p className="text-sm font-medium text-foreground">Scroll down in the menu</p>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-xl bg-charcoal-medium">
              <div className="w-8 h-8 rounded-full bg-cyan/20 flex items-center justify-center shrink-0 text-cyan font-bold text-sm">3</div>
              <p className="text-sm font-medium text-foreground">Tap "Add to Home Screen"</p>
            </div>
          </div>
          <button
            onClick={() => setShowIOSInstall(false)}
            className="w-full py-3 rounded-xl font-semibold bg-charcoal-medium border border-glass-border text-foreground hover:bg-charcoal-light transition-colors"
          >
            Got it
          </button>
        </DialogContent>
      </Dialog>
      {/* Admin Lab link — visible only to admin */}
      {user?.email === "admin@greyrockai.app" && (
        <div className="max-w-md mx-auto px-4 pb-8">
          <a
            href="/admin-lab"
            className="flex items-center justify-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors py-2"
          >
            ⚙️ Admin Lab
          </a>
        </div>
      )}
    </CommandLayout>
  );
};

export default SettingsPage;
